import React, { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Divider } from "primereact/divider";
import { TabView, TabPanel } from "primereact/tabview";
import {
  useDealerInfo,
  useUpdateDealerPassword,
  useSendDealerPasswordReset,
  usePatchDealer,
  PatchDealerRequest,
} from "hooks/useDealers";
import DeviceLinksList from "components/users/DeviceLinksList";
import DealerPasswordTab from "components/dealers/dealerDetails/DealerPasswordTab";
import DealerEditTab from "components/dealers/dealerDetails/DealerEditTab";
import { notify } from "utils/notify";
import { LoadingScreen } from "components/shared/LoadingScreen";
import { PageError } from "components/shared/PageError";
import {
  PageContainer,
  PageHeader,
  PageTitle,
  ContentContainer,
  DetailsTabView,
  DealerInfoSection,
  DealerHeader,
  DealerName,
  DealerInfoGrid,
  InfoCard,
  InfoLabel,
  InfoValue,
  StatusBadge,
  BackButton,
  SectionTitle,
} from "styles/components/dealerDetails.styles";

const DealerDetails: React.FC = () => {
  const { dealerId } = useParams<{ dealerId: string }>();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState(0);
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [isSendingReset, setIsSendingReset] = useState(false);
  const [isSavingEdit, setIsSavingEdit] = useState(false);
  const [editForm, setEditForm] = useState<PatchDealerRequest>({});

  const changePasswordMutation = useUpdateDealerPassword();
  const sendPasswordResetMutation = useSendDealerPasswordReset();
  const patchDealerMutation = usePatchDealer();

  // Fetch dealer details   with the new query hook
  const { data: dealerInfo, isLoading, error, refetch } = useDealerInfo(dealerId);

  React.useEffect(() => {
    if (dealerInfo?.dealer) {
      const d = dealerInfo.dealer;
      setEditForm({
        firstName: d.firstName,
        lastName: d.lastName,
        email: d.email,
        dealerName: d.dealerName ?? "",
        country: d.country ?? "",
      });
    }
  }, [dealerInfo]);

  const formatDate = (dateString: string) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const handleBackClick = () => {
    navigate("/dealers");
  };

  const handleSaveEdit = async () => {
    setIsSavingEdit(true);
    try {
      await patchDealerMutation.mutateAsync({
        dealerId: dealerId!,
        data: editForm,
      });
      notify.success("Dealer details saved successfully.");
    } catch (err: any) {
      notify.error(err?.response?.data?.message || "Failed to update dealer.");
    } finally {
      setIsSavingEdit(false);
    }
  };

  const handleChangePassword = (newPassword: string) => {
    if (!dealer) return;
    setIsChangingPassword(true);
    changePasswordMutation.mutate(
      { dealerId: dealer.id, newPassword },
      {
        onSuccess: () => {
          notify.success("Password updated successfully.");
        },
        onError: () => {
          notify.error("Failed to update password. Please try again.");
        },
        onSettled: () => setIsChangingPassword(false),
      },
    );
  };

  const handleSendPasswordReset = () => {
    if (!dealer) return;
    setIsSendingReset(true);
    sendPasswordResetMutation.mutate(dealer.id, {
      onSuccess: () => {
        notify.success("Password reset email sent successfully.");
      },
      onError: () => {
        notify.error("Failed to send reset email. Please try again.");
      },
      onSettled: () => setIsSendingReset(false),
    });
  };

  if (error) {
    return (
      <PageContainer>
        <PageHeader>
          <PageTitle>Dealer Details</PageTitle>
          <BackButton
            icon="pi pi-arrow-left"
            onClick={handleBackClick}
            label="Back to Dealers"
          />
        </PageHeader>
        <PageError
          title="Error loading dealer"
          message="Please try again later."
          onRetry={() => refetch()}
        />
      </PageContainer>
    );
  }

  // Acessando os dados diretamente do objeto dealerInfo, sem o subobjeto "response"
  const dealer = dealerInfo?.dealer;
  const deviceLinks = dealerInfo?.deviceLinks || [];
  const totalLinks = dealerInfo?.totalLinks || 0;
  const temporaryAccess = dealerInfo?.temporaryAccess || [];

  return (
    <PageContainer>
<PageHeader>
        <PageTitle>Dealer Details</PageTitle>
        <BackButton
          icon="pi pi-arrow-left"
          onClick={handleBackClick}
          label="Back to Dealers"
        />
      </PageHeader>

      {isLoading ? (
        <LoadingScreen message="Loading dealer details..." />
      ) : dealer ? (
        <ContentContainer>
          <DealerInfoSection>
            <DealerHeader>
              <DealerName>
                {dealer.firstName} {dealer.lastName}
              </DealerName>
              <StatusBadge status={dealer.disabled ? "disabled" : "active"}>
                {dealer.disabled ? "Disabled" : "Active"}
              </StatusBadge>
            </DealerHeader>

            <DealerInfoGrid>
              <InfoCard>
                <InfoLabel>Email</InfoLabel>
                <InfoValue>{dealer.email}</InfoValue>
              </InfoCard>
              <InfoCard>
                <InfoLabel>Dealer Name</InfoLabel>
                <InfoValue>{dealer.dealerName || "N/A"}</InfoValue>
              </InfoCard>
              <InfoCard>
                <InfoLabel>Country</InfoLabel>
                <InfoValue>{dealer.country || "N/A"}</InfoValue>
              </InfoCard>
              <InfoCard>
                <InfoLabel>Role</InfoLabel>
                <InfoValue>{dealer.role || "Standard User"}</InfoValue>
              </InfoCard>
              <InfoCard>
                <InfoLabel>Created On</InfoLabel>
                <InfoValue>{formatDate(dealer.createdAt)}</InfoValue>
              </InfoCard>
              <InfoCard>
                <InfoLabel>Last Updated</InfoLabel>
                <InfoValue>{formatDate(dealer.updatedAt)}</InfoValue>
              </InfoCard>
              <InfoCard>
                <InfoLabel>Connected Devices</InfoLabel>
                <InfoValue>{totalLinks}</InfoValue>
              </InfoCard>
            </DealerInfoGrid>
          </DealerInfoSection>

          <Divider />

          <DetailsTabView>
            <TabView
              activeIndex={activeTab}
              onTabChange={(e) => setActiveTab(e.index)}
            >
              <TabPanel header="Linked Devices">
                <SectionTitle>Device Links ({totalLinks})</SectionTitle>
                <p>
                  Devices that are linked to this dealer account. The dealer can
                  access and manage these devices.
                </p>
                <DeviceLinksList
                  deviceLinks={deviceLinks}
                  temporaryAccess={temporaryAccess}
                  dealerId={dealerId || ""}
                  isLoading={isLoading}
                  dealerName={`${dealerInfo?.dealer.firstName} ${dealerInfo?.dealer.lastName}`}
                />
              </TabPanel>
              <TabPanel header="Edit">
                <DealerEditTab
                  form={editForm}
                  isSaving={isSavingEdit}
                  onChange={setEditForm}
                  onSave={handleSaveEdit}
                />
              </TabPanel>
              <TabPanel header="Activity History">
                <div style={{ padding: "1rem" }}>
                  <p>This feature is coming soon.</p>
                </div>
              </TabPanel>
              <TabPanel header="Password">
                <DealerPasswordTab
                  dealerEmail={dealer.email}
                  isChangingPassword={isChangingPassword}
                  isSendingReset={isSendingReset}
                  onChangePassword={handleChangePassword}
                  onSendReset={handleSendPasswordReset}
                />
              </TabPanel>
            </TabView>
          </DetailsTabView>
        </ContentContainer>
      ) : (
        <PageError title="Dealer not found" message="Dealer information was not found." />
      )}
    </PageContainer>
  );
};

export default DealerDetails;
